import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { NotificationService } from 'src/app/core/services/notification.service';
import { DocumentJobAdminService } from 'src/app/shared/services/document-job-admin.service';
import {
  AdminDocumentJobDetail,
  DocumentJobErrorMetadata,
  DocumentJobStatus,
  DocumentJobSuccessMetadata,
  DocumentJobType,
} from 'src/app/admin/models/document-job.model';

@Component({
  selector: 'app-document-job-detail',
  imports: [
    CommonModule,
    RouterLink,
    ButtonModule,
    CardModule,
    ToastModule,
    TooltipModule,
  ],
  templateUrl: './document-job-detail.component.html',
})
export class DocumentJobDetailComponent implements OnInit {
  private jobService = inject(DocumentJobAdminService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private notificationService = inject(NotificationService);

  DocumentJobStatus = DocumentJobStatus;
  DocumentJobType = DocumentJobType;

  job = signal<AdminDocumentJobDetail | null>(null);
  loading = signal(true);

  jobType = computed<DocumentJobType | null>(() => {
    const j = this.job();
    if (!j) return null;
    return j.batchId.startsWith('single-')
      ? DocumentJobType.SINGLE
      : DocumentJobType.BATCH;
  });

  durationSeconds = computed<number | null>(() => {
    const j = this.job();
    if (!j || !j.processedAt) return null;
    const created = new Date(j.createdAt).getTime();
    const processed = new Date(j.processedAt).getTime();
    if (isNaN(created) || isNaN(processed)) return null;
    return Math.round((processed - created) / 1000);
  });

  errorMetadata = computed<DocumentJobErrorMetadata | null>(() => {
    const j = this.job();
    if (!j || !j.metadata) return null;
    const meta = j.metadata as DocumentJobErrorMetadata;
    return meta.errorCode || meta.errorContext || meta.failedAt ? meta : null;
  });

  successMetadata = computed<DocumentJobSuccessMetadata | null>(() => {
    const j = this.job();
    if (!j || j.status !== DocumentJobStatus.COMPLETED || !j.metadata)
      return null;
    return j.metadata as DocumentJobSuccessMetadata;
  });

  errorContextJson = computed<string | null>(() => {
    const err = this.errorMetadata();
    if (!err?.errorContext) return null;
    try {
      return JSON.stringify(err.errorContext, null, 2);
    } catch {
      return null;
    }
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) this.loadJob(id);
  }

  loadJob(id: string): void {
    this.loading.set(true);
    this.jobService.getJob(id).subscribe({
      next: (response) => {
        this.job.set(response.data);
        this.loading.set(false);
      },
      error: (err) => {
        this.loading.set(false);
        if (err.status === 404) {
          this.notificationService.error('Document job not found');
          this.router.navigate(['/admin/document-jobs']);
        } else {
          this.notificationService.error('Failed to load document job');
        }
      },
    });
  }

  openBrochure(): void {
    const url = this.job()?.brochureUrl;
    if (!url) {
      this.notificationService.error('Brochure URL is not available');
      return;
    }
    window.open(url, '_blank', 'noopener');
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '--';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      timeZone: 'Europe/Brussels',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  formatDuration(seconds: number | null): string {
    if (seconds === null) return '--';
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m ${s}s`;
  }

  getStatusClasses(status: DocumentJobStatus): string {
    const classes: Record<DocumentJobStatus, string> = {
      [DocumentJobStatus.PENDING]: 'bg-greyscale-100 text-greyscale-700',
      [DocumentJobStatus.PROCESSING]: 'bg-primary-100 text-primary-700',
      [DocumentJobStatus.COMPLETED]: 'bg-validation-100 text-validation-700',
      [DocumentJobStatus.FAILED]: 'bg-error-100 text-error-700',
    };
    return classes[status] || 'bg-greyscale-100 text-greyscale-700';
  }

  back(): void {
    this.router.navigate(['/admin/document-jobs']);
  }
}
